import { type AppErrorCode, isAppErrorCode } from '@fos/shared/errors';
import { ApiError, NetworkError } from '../../lib/api';

interface ErrorBody {
  error?: { code?: unknown; message?: unknown };
}

async function readErrorBody(response: Response): Promise<ErrorBody | null> {
  try {
    return (await response.json()) as ErrorBody;
  } catch {
    return null;
  }
}

/**
 * Pobranie eksportu (REST `/api/exports/time.csv|pdf`): zwykły GET z ciasteczkiem sesji. Błąd serwera (np. brak
 * „Eksportuje dane”) wraca jako `ApiError` z polskim komunikatem, więc UI może pokazać toast zamiast strony z JSON.
 */
export async function downloadExport(url: string, fallbackName: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'same-origin' });
  } catch {
    throw new NetworkError();
  }
  if (!response.ok) {
    const body = await readErrorBody(response);
    const code: AppErrorCode = isAppErrorCode(body?.error?.code)
      ? body.error.code
      : response.status === 403
        ? 'FORBIDDEN'
        : response.status === 401
          ? 'UNAUTHENTICATED'
          : 'INTERNAL';
    throw new ApiError({
      code,
      status: response.status,
      message: typeof body?.error?.message === 'string' ? body.error.message : undefined,
    });
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
